import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useStyles } from './PersonaCommunity.style.js';
import moment from 'moment';
import _ from 'lodash';
import InfiniteScroll from 'react-infinite-scroll-component';
import { appActions } from '../../actions/app.actions.js';
import {
  fetchAssistantsCommunityItems,
  fetchAssistantsPublicItems,
  searchAssistantsAndThreads,
} from '../../services/page.services';
import { Grid } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import PersonaHistory from './PersonaHistory';
import { checkValidLoginStatus } from '../../utils/user.utils';

function Community() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(state => state.user);
  const userId = user?.id;

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [userThreads, setUserThreads] = useState([]);
  const [initialThreads, setInitialThreads] = useState([]);
  const [communityQuery, setCommunityQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedSortingMethod, setSelectedSortingMethod] = useState('trending');

  const handleSortChange = event => {
    setSelectedSortingMethod(event.target.value);
  };

  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  };

  const debouncedCommunityQuery = useDebounce(communityQuery, 50);

  useEffect(() => {
    initData();
  }, [selectedSortingMethod, debouncedCommunityQuery, userId]);

  const initData = async () => {
    try {
      let response;
      let userThreads;

      if (debouncedCommunityQuery) {
        if (userId) {
          const searchResults = await searchAssistantsAndThreads(debouncedCommunityQuery, selectedSortingMethod);
          userThreads = searchResults.userThreads;
          response = searchResults.assistants;
        } else {
          const searchResults = await fetchAssistantsPublicItems(debouncedCommunityQuery, selectedSortingMethod);
          response = searchResults;
        }
      } else {
        if (userId) {
          response = await fetchAssistantsCommunityItems(selectedSortingMethod);
          userThreads = (await searchAssistantsAndThreads('')).userThreads;
        }
        else {
          response = await fetchAssistantsPublicItems('', selectedSortingMethod);
        }
      }
      setUserThreads(userThreads);
      setData(response);
      setFilteredData(response);
      const usersData = response.reduce((acc, item) => {
        acc[item.user.id] = item.user;
        return acc;
      }, {});
      setUsers(usersData);
    } catch (err) {
      dispatch(appActions.openMessageModal(t('historyTab.modal.error'), false, true));
      if (err.name !== 'CanceledError') {
        dispatch(appActions.openMessageModal(t('historyTab.modal.error'), false, true));
      }
    }
  };

  const handleTemplateEdit = () => {
    navigate('/template-edit');
  };

  const handleCreate = () => {
    if(!userId){
      checkValidLoginStatus(userId, dispatch);
      return;
    }
    navigate('/create-persona');
  };
  const handleChat = (name, image, id) => {
    if(!userId){
      checkValidLoginStatus(userId, dispatch);
      return;
    }
    navigate('/chat', {
      state: {
        modelName: name,
        image: image,
        assistantId: id,
      },
    });
  }

  const classes = useStyles();

  const findUserName = userId => {
    const user = users[userId];
    return user ? user.name : 'Unknown';
  };
  const findUserID = userId => {
    const user = users[userId];
    console.log(user);
    return user ? user.id : 'Unknown';
  };

  const handleCommunityQueryChange = event => {
    setCommunityQuery(event.target.value);
  };

  return (
    <div className={classes.main}>
      <div className={classes.titleContainer}>
        <div className={classes.pageTitle}>{t('personaCommunityTab.title')}</div>
        {findUserID(userId) === '06f660e6-ecdf-4760-acf8-9c9e050ace77' && (
          <div className={classes.button} onClick={handleTemplateEdit} style={{ marginLeft: '10px' }}>
           프롬프트 템플릿 수정
          </div>
        )}
        <Grid item xs={12} sm={3} md={12} container justifyContent="flex-end">
          <div className={classes.button} onClick={handleCreate}>
            {t('personaCommunityTab.create')}
          </div>
        </Grid>
      </div>
      <Grid container className={classes.topBar}>
        <Grid item xs={12} sm={7} md={8} className={classes.searchbarContainer}>
          <input
            className={classes.searchbar}
            placeholder={t('personaCommunityTab.searchbar')}
            value={communityQuery}
            onChange={handleCommunityQueryChange}
          />
          <SearchIcon className={classes.searchIcon} />
        </Grid>
      </Grid>

      <Grid container className={classes.container}>
        {userThreads && userThreads.length > 0 && (
          <Grid item xs={12} md={6} className={classes.leftColumn}>
            <PersonaHistory userThreads={userThreads} />
          </Grid>
        )}

        <Grid item xs={12} md={userThreads && userThreads.length > 0 ? 6 : 12} className={classes.rightColumn}>
          <Grid container className={classes.recentContainer}>
            <Grid item className={classes.titleContainer}>
              <div className={classes.pageSmallTitle}>
                {debouncedCommunityQuery && debouncedCommunityQuery.length > 0
                  ? t('personaCommunityTab.searchResult')
                  : t('personaCommunityTab.sortOptions.0')}
              </div>
            </Grid>

            <Grid container item xs={4} sm={4} md={4} display="flex" justifyContent="flex-end">
              <FormControl size="small" className={classes.sortSelect}>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={selectedSortingMethod}
                  classes={{ select: classes.selectInput, icon: classes.selectIcon }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        padding: '0px', // 기본 패딩을 0으로 설정하여 제거
                        margin: '0px', // 기본 마진도 제거
                        backgroundColor: '#101012', // 배경색 변경
                      },
                    },
                    MenuListProps: {
                      style: {
                        paddingTop: '0px', // 패딩 위쪽 제거
                        paddingBottom: '0px', // 패딩 아래쪽 제거
                      },
                    },
                  }}
                  onChange={handleSortChange}
                >
                  <MenuItem
                    className={classes.sortItem}
                    value={'trending'}
                    style={{ color: '#8c8e97', backgroundColor: '#101012' }}
                  >
                    {t('personaCommunityTab.simpleSortOptions.0')}
                  </MenuItem>
                  <MenuItem
                    className={classes.sortItem}
                    value={'latest'}
                    style={{ color: '#8c8e97', backgroundColor: '#101012' }}
                  >
                    {t('personaCommunityTab.simpleSortOptions.1')}
                  </MenuItem>
                  <MenuItem
                    className={classes.sortItem}
                    value={'name'}
                    style={{ color: '#8c8e97', backgroundColor: '#101012' }}
                  >
                    {t('personaCommunityTab.simpleSortOptions.2')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <InfiniteScroll
            className={classes.infiniteScroll}
            dataLength={filteredData ? filteredData.length : 0}
            loader={
              <div className={classes.pagination} key={0}>
                {t('historyTab.pagination.loader')}
              </div>
            }
          >
            <Grid container className={classes.cardGrid}>
              {filteredData?.map(({ id, name, userId, createdAt, updatedAt, image, description }) => {
                return (
                  <Grid
                  className={`${classes.card} ${!userThreads || userThreads.length === 0 ? classes.fullWidthCard : ''}`}
                    item
                    container
                    key={id}
                    onClick={() => {
                      handleChat(name, image, id);
                    }}
                  >
                    {image && (
                      <Grid className={classes.imgContainer} item xs="auto" sm={3} md={3}>
                        <img className={classes.coverImg} src={image} alt="cover-img" />
                      </Grid>
                    )}
                    <Grid container className={classes.cardSecondColumn} item xs sm={9} md={9}>
                      <Grid container item>
                        <Grid container className={classes.cardLabelContainer} item xs={10} sm={10} md={10}>
                          <div className={classes.cardLabel}>{name}</div>
                        </Grid>
                      </Grid>
                      <Grid container item>
                        <div className={classes.cardDescription}>{description}</div>
                      </Grid>
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>
          </InfiniteScroll>
        </Grid>
      </Grid>
    </div>
  );
}

export default Community;
